import React, { useState, useEffect } from "react";
import clsx from "clsx";
import html2canvas from "html2canvas";
import { Progress } from "Components/Progress/Progress";
import { Button } from "Components/Button/Button";
import { FileSymlinkIcon } from "Components/Icons/Documents/FileSymlinkIcon";
import { Dialog, CircularProgress } from "@mui/material";
import { CloseIcon } from "Components/Icons/Miscellaneous/CloseIcon";
import { EXTENSION_TO_ICON_MAP, TIPOS_CONTENIDO } from "Constantes/constants";
import { DynamicIcon } from "Components/DynamicIcon/DynamicIcon";
import deviceOrientation from "Recursos/device- orientation.webp";
import "./LearningCard.scss";

/**
 * Componente card de aprendizaje que muestra contenido de curso con soporte multimedia
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.titulo="Curso"] - Título del curso
 * @param {string} [props.categoria="Categoría"] - Categoría del curso
 * @param {number} [props.progress=0] - Progreso del curso (0-100)
 * @param {boolean} [props.isLoadingProgress=false] - Indica si el progreso está cargando
 * @param {Function} [props.onCloseMaterial] - Callback ejecutado cuando se cierra un material, recibe el item como parámetro
 * @param {Array} [props.contentItems=[]] - Array de elementos de contenido del curso
 * @param {Function} [props.onBack] - Callback para el botón de regresar
 * @param {Function} [props.onAction] - Callback para el botón de acción (siguiente/finalizar)
 * @param {boolean} [props.isLastContent=false] - Indica si es el último contenido (muestra "FINALIZAR CURSO" en vez de "SIGUIENTE")
 * @param {boolean} [props.actionButtonDisabled=false] - Deshabilita el botón de acción
 * @param {boolean} [props.backButtonDisabled=false] - Deshabilita el botón de regresar
 * @param {boolean} [props.isLoading=false] - Indica si está cargando (para mostrar spinner en botón)
 * @returns {JSX.Element} Componente LearningCard
 */
export const LearningCard = ({
  titulo = "Curso",
  categoria = "Categoría",
  progress = 0,
  isLoadingProgress = false,
  onCloseMaterial,
  contentItems = [],
  className = "",
  onBack,
  onAction,
  isLastContent = false,
  actionButtonDisabled = false,
  backButtonDisabled = false,
  isLoading = false,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentContent, setCurrentContent] = useState(null);
  const [isPortrait, setIsPortrait] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  const openContent = (content) => {
    if (!content) return;
    setCurrentContent(content);
    setOpenModal(true);
  };

  const mainContent = contentItems?.[0];

  useEffect(() => {
    const checkOrientation = () => {
      if (window.screen && window.screen.orientation) {
        setIsPortrait(window.screen.orientation.type.startsWith("portrait"));
      }
    };

    checkOrientation();

    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener("change", checkOrientation);
      return () => {
        window.screen.orientation.removeEventListener(
          "change",
          checkOrientation
        );
      };
    }
  }, []);

  useEffect(() => {
    if (
      mainContent?.tipoContenido === TIPOS_CONTENIDO.E_LEARNING.NOMBRE &&
      !previewImage &&
      !isGeneratingPreview
    ) {
      generatePreviewImage(mainContent.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainContent]);

  const closeModal = () => {
    const contentToClose = currentContent;
    setOpenModal(false);
    setCurrentContent(null);

    if (onCloseMaterial) {
      onCloseMaterial(contentToClose);
    }
  };

  const supportMaterials = contentItems.filter(
    (item) => item.idContenidoCurso !== mainContent?.idContenidoCurso
  );

  const getFileExtension = (url) => {
    if (!url) return "";

    try {
      const urlObj = new URL(url);
      return urlObj.pathname.split(".").pop()?.toLowerCase() || "";
    } catch (error) {
      return url.split(".").pop()?.split("?")[0]?.toLowerCase() || "";
    }
  };

  const getIconForContentType = (url) => {
    if (url) {
      const extension = getFileExtension(url);
      const iconName = EXTENSION_TO_ICON_MAP[extension];
      if (iconName) {
        return <DynamicIcon iconName={iconName} />;
      }
    }

    return <FileSymlinkIcon />;
  };

  const generatePreviewImage = async (url) => {
    setIsGeneratingPreview(true);
    try {
      const tempIframe = document.createElement("iframe");
      tempIframe.src = url;
      tempIframe.allow = "autoplay 'none'";
      Object.assign(tempIframe.style, {
        position: "absolute",
        left: "-9999px",
        width: "1200px",
        height: "800px",
      });
      document.body.appendChild(tempIframe);

      await new Promise((resolve) => (tempIframe.onload = resolve));

      const baseUrl = url.substring(0, url.lastIndexOf("/") + 1);
      const base = tempIframe.contentDocument.createElement("base");
      base.href = baseUrl;
      tempIframe.contentDocument.head.prepend(base);

      await new Promise((resolve) => setTimeout(resolve, 500));

      const iframeDoc =
        tempIframe.contentDocument || tempIframe.contentWindow.document;
      const canvas = await html2canvas(iframeDoc.body, {
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        imageTimeout: 5000,
        width: 1200,
        height: 800,
      });

      setPreviewImage(canvas.toDataURL("image/png", 1.0));
      document.body.removeChild(tempIframe);
    } catch (error) {
      console.error("Error generando preview:", error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const renderContent = (content, isPreview = false) => {
    if (!content || !content.url) return null;

    const isValidUrl =
      content.url.startsWith("http://") || content.url.startsWith("https://");

    if (content.tipoContenido === TIPOS_CONTENIDO.IMAGEN.NOMBRE) {
      if (!isValidUrl) {
        return (
          <div className="learning-card__placeholder">
            {getIconForContentType(content.url)}
            <span>URL de imagen no válida</span>
          </div>
        );
      }
      return (
        <div className="learning-card__image-container">
          <img
            src={content.url}
            alt={content.titulo}
            className="learning-card__image"
          />
        </div>
      );
    }

    if (content.tipoContenido === TIPOS_CONTENIDO.VIDEO.NOMBRE) {
      if (!isValidUrl) {
        return (
          <div className="learning-card__placeholder">
            {getIconForContentType(content.url)}
            <span>URL de video no válida</span>
          </div>
        );
      }
      return (
        <video
          src={content.url}
          controls
          className="learning-card__video"
          style={{ width: "100%", height: "100%" }}
        >
          Tu navegador no soporta la reproducción de videos.
        </video>
      );
    }

    if (content.tipoContenido === TIPOS_CONTENIDO.PDF.NOMBRE) {
      if (!isValidUrl) {
        return (
          <div className="learning-card__placeholder">
            {getIconForContentType(content.url)}
            <span>URL de PDF no válida</span>
          </div>
        );
      }
      return (
        <iframe
          src={content.url}
          title={content.titulo}
          allowFullScreen
          className="learning-card__pdf"
          style={{ width: "100%", height: "100%" }}
        />
      );
    }

    if (isValidUrl) {
      if (
        isPreview &&
        content.tipoContenido === TIPOS_CONTENIDO.E_LEARNING.NOMBRE
      ) {
        if (previewImage) {
          return (
            <img
              src={previewImage}
              alt={`Preview de ${content.titulo}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          );
        }
        return (
          <div className="learning-card__placeholder">
            {isGeneratingPreview ? (
              <span>Generando vista previa...</span>
            ) : (
              <>
                {getIconForContentType(content.url)}
                <span>{content.titulo}</span>
              </>
            )}
          </div>
        );
      }

      return (
        <iframe src={content.url} title={content.titulo} allowFullScreen />
      );
    }

    return (
      <div className="learning-card__placeholder">
        {getIconForContentType(content.url)}
        <span>URL no válida</span>
      </div>
    );
  };

  return (
    <>
      <div className={clsx("learning-card-wrapper", className)}>
        <div className="learning-card">
          <div className="learning-card__header">
            <h2 className="learning-card__title">{titulo}</h2>
            <div className="learning-card__divider" />
            <p className="learning-card__meta">
              <span className="learning-card__meta-category">{categoria}</span>
            </p>
          </div>

          <Progress
            variant="percentage"
            current={progress}
            total={100}
            isLoading={isLoadingProgress}
          />

          <div className="learning-card__iframe-container">
            {mainContent ? (
              <>
                {renderContent(mainContent, true)}
                <div
                  className="learning-card__iframe-overlay"
                  onClick={() => openContent(mainContent)}
                />
              </>
            ) : (
              <div className="learning-card__placeholder">
                <span>Contenido no disponible</span>
              </div>
            )}
          </div>

          {supportMaterials.length > 0 && (
            <div className="learning-card__support">
              <p className="learning-card__support-title">Material de apoyo:</p>
              <div className="learning-card__support-btns">
                {supportMaterials.map((item) => (
                  <Button
                    key={item.idContenidoCurso}
                    variant="multimedia"
                    size="medium"
                    onClick={() => openContent(item)}
                    title={item.titulo}
                  >
                    {getIconForContentType(item.url)}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="learning-card__actions">
          <Button
            variant="secundario-cta"
            disabled={backButtonDisabled}
            onClick={onBack}
          >
            REGRESAR
          </Button>
          <Button disabled={actionButtonDisabled} onClick={onAction}>
            {isLoading && (
              <CircularProgress size={20} sx={{ marginRight: 1 }} />
            )}
            {isLastContent ? "FINALIZAR" : "SIGUIENTE"}
          </Button>
        </div>
      </div>

      <Dialog
        open={openModal}
        onClose={closeModal}
        fullWidth
        maxWidth="lg"
        className="learning-modal"
        scroll="body"
      >
        <div aria-label="close" onClick={closeModal} className="close-button">
          <CloseIcon />
        </div>

        {isPortrait && (
          <div className="learning-modal__device-orientation">
            <img src={deviceOrientation} alt="Device Orientation" />
            <p>Gira tu Pantalla</p>
          </div>
        )}

        {currentContent && !isPortrait && renderContent(currentContent)}
      </Dialog>
    </>
  );
};
